import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Customer } from '@api/models/customer.model';
import { CardComponent } from '@ui/card/card.component';
import { CustomersService } from '@api/index';
import { IndexResponse } from '@api/models/response.model';
import { TableComponent } from '@ui/table/table.component';
import { InputComponent } from '@ui/input/input.component';
import { ButtonComponent } from '@ui/button/button.component';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NotificationService } from '@ui/notification/notification.service';
import { AppService } from '../../app.service';
@Component({
  selector: 'app-customers',
  standalone: true,
  imports: [
    CardComponent,
    TableComponent,
    InputComponent,
    ButtonComponent,
    FormsModule,
  ],
  template: `
    <div
      class="flex flex-col gap-4 h-[calc(100vh-theme(spacing.24))] overflow-hidden"
    >
      <ui-card>
        <div class="flex justify-between items-center">
          <h1 class="text-lg text-slate-900">Klanten</h1>
        </div>
      </ui-card>
      <ui-card>
        <div class="flex flex-col gap-4 justify-between">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <ui-input
              label="Naam"
              placeholder="Zoek op naam..."
              [(ngModel)]="filters.name"
              (ngModelChange)="onFilterChange()"
            ></ui-input>

            <ui-input
              label="Straat"
              placeholder="Zoek op straat..."
              [(ngModel)]="filters.address_street"
              (ngModelChange)="onFilterChange()"
            ></ui-input>

            <ui-input
              label="Postcode"
              placeholder="Zoek op postcode..."
              [(ngModel)]="filters.address_zipcode"
              (ngModelChange)="onFilterChange()"
            ></ui-input>
          </div>
          <div class="flex gap-2 items-end" style="padding-bottom: 2px;">
            <ui-button
              variant="primary"
              [icon]="'filter'"
              (onClick)="applyFilters()"
              >Filter toepassen</ui-button
            >
            <ui-button
              variant="ghost"
              [icon]="'refresh'"
              (onClick)="resetFilters()"
              >Reset filters</ui-button
            >
          </div>
        </div>
      </ui-card>
      <div class="h-full overflow-hidden">
        <ui-table
          [columns]="columns"
          [data]="customers()"
          [loading]="loading()"
          (onRowClick)="onRowClick($event)"
          (onPageChange)="onPageChange($event)"
          [showPagination]="true"
          [meta]="meta()"
          [bordered]="false"
        ></ui-table>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomersComponent {
  router = inject(Router);
  notificationService = inject(NotificationService);
  customersService = inject(CustomersService);
  appService = inject(AppService);

  loading = signal<boolean>(false);
  customers = signal<Customer[]>([]);
  meta = signal<IndexResponse<Customer>['meta'] | null>(null);

  columns = [
    { key: 'number', label: 'Nummer' },
    { key: 'name', label: 'Naam' },
    { key: 'address_street', label: 'Straat' },
    { key: 'address_house_number', label: 'Huisnummer' },
    { key: 'address_zipcode', label: 'Postcode' },
    { key: 'address_city', label: 'Plaats' },
  ];

  filters = {
    name: '',
    address_street: '',
    address_zipcode: '',
  };

  constructor() {
    effect(() => {
      if (this.appService.municipalityId()) {
        this.getCustomers();
      }
    }, { allowSignalWrites: true });
  }

  getCustomers(page: number = 1, filters: any = {}) {
    this.loading.set(true);
    try {
      this.customersService
        .getCustomers(page, filters)
        .subscribe((customers) => {
          this.customers.set(customers.data);
          this.meta.set(customers.meta);
          this.loading.set(false);
        });
    } catch (error: any) {
      this.notificationService.error(error.message);
      this.loading.set(false);
    }
  }

  onRowClick(event: any) {
    this.router.navigate(['/customers', event.id]);
  }

  onPageChange(event: any) {
    this.getCustomers(event, this.filters);
  }

  onFilterChange() {}

  applyFilters() {
    this.getCustomers(1, this.filters);
  }

  resetFilters() {
    this.filters = { name: '', address_street: '', address_zipcode: '' };
    this.getCustomers(1);
  }
}
